/**
 * TRACI-1012: LivePerson Secure Forms. When the rep sends a secure form from LivePerson, the
 * backend gets its one-time keys and hands us LivePerson's own form page address. We show that
 * page in a frame over the chat. It is LivePerson's page on LivePerson's domain: nothing in our
 * chat (our code, the Adobe tag) can read what the customer types into it. On submit the page
 * posts us a submission id (never card data), which we pass to the backend so the rep can view it.
 *
 * Close does not throw the form away: it tucks it into a small "Payment form · Open" button so the
 * customer can come back to it. The frame stays loaded while tucked away (its keys are one-time,
 * so it is never reloaded). It goes for good once submitted or when the live chat ends.
 */
import React, { useEffect, useRef, useState } from 'react';
import styles from './SecureFormModal.module.css';
import { formKind, messageOriginOk, readOwnFormState } from './secureFormOrigins';

// Same name as installSecureForm's SECURE_FORM_STATUS_EVENT (kept here to avoid an import cycle).
const STATUS_EVENT = 'traci:secure-form-status';

export interface SecureFormRequest {
  invitationId: string;
  title?: string;
  url: string;
  agentName?: string;
  reportSubmitted?: (invitationId: string, submissionId: string) => void;
}

type Phase = 'form' | 'done' | 'error';

export function SecureFormModal({ request, onClose }: { request: SecureFormRequest; onClose: () => void }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [phase, setPhase] = useState<Phase>('form');
  const [minimized, setMinimized] = useState(false);
  const reported = useRef(false);
  // CPOC-143: our own card form (Connect chats) is framed with a sandbox and no device
  // permissions; LivePerson's page keeps the plain frame it has always had.
  const own = formKind(request.url) === 'own';

  useEffect(() => {
    const kind = formKind(request.url);
    const finish = () => {
      window.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail: { state: 'submitted', agentName: request.agentName } }));
      setPhase('done');
      setMinimized(false);
    };
    const onMessage = (e: MessageEvent) => {
      if (!frame.current || e.source !== frame.current.contentWindow) return;
      if (!kind) return;
      if (kind === 'own') {
        if (!messageOriginOk(e.origin, request.url)) return;
        // CPOC-143: our card form says only {state}; the shape is strict and nothing is kept.
        const state = readOwnFormState(e.data);
        // Stay on the form until the rep's desk confirms: it shows 'received' or 'ask your rep to resend'.
        if (state === 'received' && !reported.current) { reported.current = true; finish(); }
        else if (state === 'error') setPhase('error');
        return;
      }
      // LivePerson's path is deliberately unchanged: no origin check until a sandbox form shows which
      // origin its page posts from (its payload carries a gatewayDomain), so prod LP can't regress.
      // LivePerson's page posts a JSON string {state, token, gatewayDomain}, or a bare state.
      let msg: { state?: string; token?: string } = {};
      try { msg = typeof e.data === 'string' ? JSON.parse(e.data) : (e.data || {}); } catch { msg = { state: String(e.data) }; }
      const state = String(msg.state || 'submit').toLowerCase();
      if (msg.token && !reported.current) {
        reported.current = true;
        request.reportSubmitted?.(request.invitationId, msg.token);
        finish();
      } else if (state.includes('error')) {
        setPhase('error');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [request]);

  return (
    <>
      {minimized && (
        <button type="button" className={styles.reopen} onClick={() => setMinimized(false)}
          aria-label="Open the payment form your rep sent">
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><rect x="2.5" y="7" width="11" height="7.5" rx="1.5" fill="currentColor" /><path d="M5 7V5a3 3 0 0 1 6 0v2" stroke="currentColor" strokeWidth="1.6" fill="none" /></svg>
          <span>Payment form</span>
          <span className={styles.reopenAction}>Open</span>
        </button>
      )}
      <div className={styles.backdrop} role="presentation" hidden={minimized}>
        <div className={styles.sheet} role="dialog" aria-modal="true" aria-labelledby="tr-sf-title">
          <div className={styles.header}>
            <span className={styles.lock} aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 16 16"><rect x="2.5" y="7" width="11" height="7.5" rx="1.5" fill="currentColor" /><path d="M5 7V5a3 3 0 0 1 6 0v2" stroke="currentColor" strokeWidth="1.6" fill="none" /></svg>
            </span>
            <div className={styles.headText}>
              <h2 id="tr-sf-title" className={styles.title}>Payment details</h2>
              <p className={styles.subtitle}>Enter your card to complete your order</p>
            </div>
          </div>
          {phase === 'done' ? (
            <div className={styles.body}>
              <div className={styles.done} role="status">
                <svg width="28" height="28" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="9" fill="currentColor" /><path d="M6 10.5l2.6 2.6L14 7.8" stroke="#fff" strokeWidth="2" fill="none" /></svg>
                <div>
                  <p className={styles.doneTitle}>Payment details received</p>
                  <p className={styles.doneText}>We'll confirm your order in the chat in a moment.</p>
                </div>
              </div>
              <button type="button" className={styles.primary} onClick={onClose}>Back to chat</button>
            </div>
          ) : (
            <div className={styles.frameBody}>
              {phase === 'error' && (
                <p className={styles.failed} role="alert">Something went wrong with the payment form. Please close it and ask your rep to send it again.</p>
              )}
              <iframe ref={frame} className={styles.frame} src={request.url}
                {...(own ? { sandbox: 'allow-scripts allow-same-origin', allow: '' } : {})} title={request.title || 'Payment details'} />
              <button type="button" className={styles.secondary} onClick={() => setMinimized(true)}>Close</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
